import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaRobot, 
  FaPlus, 
  FaEdit, 
  FaServer, 
  FaUserCog, 
  FaUserCircle, 
  FaChevronRight, 
  FaTools, 
  FaExclamationTriangle, 
  FaComments, 
  FaDatabase, 
  FaArrowRight, 
  FaBolt, 
  FaCog, 
  FaCode,
  FaBrain,
  FaProjectDiagram
} from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import brainLogo from '../assets/IMG_0118.png';
import './Homepage.css';

function Homepage() {
  const { isAdmin, isLoading: authLoading, currentUser } = useContext(AuthContext);
  
  // State for recent agents
  const [recentAgents, setRecentAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Fetch recent agents on component mount
  useEffect(() => {
    const fetchRecentAgents = async () => {
      try {
        setLoadingAgents(true);
        const response = await api.getAgents();
        
        if (response && response.agents && Array.isArray(response.agents)) {
          // Sort by commissioned status and deployment status to prioritize active agents
          const sortedAgents = response.agents
            .filter(agent => agent.agentName || agent.repoName)
            .sort((a, b) => {
              // Prioritize commissioned agents
              if (a.commissioned !== b.commissioned) {
                return b.commissioned ? 1 : -1;
              }
              // Then prioritize deployed agents
              const aDeployed = a.deployedInDev || a.deployedInProd;
              const bDeployed = b.deployedInDev || b.deployedInProd;
              if (aDeployed !== bDeployed) {
                return bDeployed ? 1 : -1;
              }
              // Finally sort by name
              const aName = a.agentName || a.repoName;
              const bName = b.agentName || b.repoName;
              return aName.localeCompare(bName);
            })
            .slice(0, 2);
          
          setRecentAgents(sortedAgents);
        }
      } catch (error) {
        console.error('Error fetching recent agents:', error);
      } finally {
        setLoadingAgents(false);
      }
    };

    fetchRecentAgents();
  }, []);

  // Define features with Tesla-inspired styling
  // isAdmin is now stable when this runs because AuthContext blocks render until auth is resolved
  const features = useMemo(() => {
    const baseFeatures = [
      {
        id: 1,
        title: 'Neural Chat Interface',
        description: 'Engage with our advanced AI agent through an intuitive chat interface. Create agents, manage complex workflows, and accelerate development with intelligent assistance.',
        icon: <FaComments />,
        link: '/chat',
        buttonText: 'Initialize Chat',
        gradient: 'from-electric-cyan to-cyber-blue'
      },
      {
        id: 2,
        title: 'Knowledge Matrix',
        description: 'Access and expand the intelligent knowledge base with neural classification and semantic search capabilities. Power your agents with advanced data intelligence.',
        icon: <FaDatabase />,
        link: '/knowledge-base',
        buttonText: 'Access Matrix',
        gradient: 'from-cyber-blue to-electric-cyan'
      },
      {
        id: 3,
        title: 'Agent Evolution Lab',
        description: 'Enhance existing agents with precision configuration tools. Modify neural networks, update behavioral patterns, and optimize performance metrics.',
        icon: <FaEdit />,
        link: '/agents/develop',
        buttonText: 'Enter Lab',
        showRecentAgents: true,
        gradient: 'from-electric-cyan to-cyber-blue'
      },
      {
        id: 8,
        title: 'AI Models Center',
        description: 'Manage LLM configurations, levels, and performance metrics. Configure model providers, track costs, and test new models in a centralized hub.',
        icon: <FaBrain />,
        link: '/ai-models',
        buttonText: 'Manage Models',
        gradient: 'from-cyber-blue to-electric-cyan'
      },
      {
        id: 9,
        title: 'Agent Structure',
        description: 'Visualize agent tree structure, view orphan components, and manage pending executions in a comprehensive dashboard.',
        icon: <FaProjectDiagram />,
        link: '/agent-structure',
        buttonText: 'View Structure',
        gradient: 'from-cyber-blue to-electric-cyan'
      },
      {
        id: 4,
        title: 'Deployment Engine',
        description: 'Deploy agents to development or production environments with automated infrastructure scaling. Monitor deployment telemetry and manage lifecycle events.',
        icon: <FaServer />,
        link: '/agents/deploy',
        buttonText: 'Deploy Systems',
        gradient: 'from-cyber-blue to-electric-cyan'
      },
      {
        id: 5,
        title: 'Agent Registry',
        description: 'Explore the complete catalog of available agents, their capabilities, performance metrics, and operational status in real-time.',
        icon: <FaRobot />,
        link: '/agents/list',
        buttonText: 'Browse Registry',
        underConstruction: true,
        gradient: 'from-electric-cyan to-cyber-blue'
      },
      {
        id: 6,
        title: 'Agent Genesis',
        description: 'Create new agents from advanced templates or custom specifications. Configure neural architectures and define behavioral parameters for optimal performance.',
        icon: <FaPlus />,
        link: '/agents/add',
        buttonText: 'Create Agent',
        underConstruction: true,
        gradient: 'from-cyber-blue to-electric-cyan'
      }
    ];

    // Add admin feature if user is an admin
    if (isAdmin) {
      baseFeatures.push({
        id: 7,
        title: 'System Administration',
        description: 'Advanced user management with role-based access control. Monitor system telemetry, manage permissions, and oversee platform operations.',
        icon: <FaUserCog />,
        link: '/user-admin',
        buttonText: 'Admin Console',
        gradient: 'from-electric-cyan to-cyber-blue',
        adminOnly: true
      });
    }

    return baseFeatures;
  }, [isAdmin]);

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--midnight-steel, #0a0e1a)'
      }}>
        <Spinner />
      </div>
    );
  }

  // Function to get agent name for display
  const getAgentDisplayName = (agent) => {
    return agent.agentName || agent.repoName || 'Unnamed Agent';
  };

  // Function to get agent navigation path
  const getAgentPath = (agent) => {
    const agentName = agent.agentName || agent.repoName;
    return `/parent/${agentName}`;
  };

  return (
    <div className="homepage-container">
      {/* Animated grid background */}
      <div className="grid-background" />
      
      <div className="welcome-section">
        <div className="brand-logo-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '1.5rem',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <img 
            src={brainLogo} 
            alt="Brain Logo" 
            style={{ 
              width: '80px', 
              height: '80px',
              filter: 'drop-shadow(0 0 20px rgba(0, 229, 255, 0.6))',
              borderRadius: 'var(--border-radius-lg)',
              border: '2px solid var(--electric-cyan)',
              padding: '8px',
              background: 'linear-gradient(145deg, var(--midnight-steel), var(--shadow-blue))',
              backdropFilter: 'blur(20px)'
            }} 
          />
          <h2 style={{ margin: 0 }}>Agent Developer</h2>
        </div>
        <p>
          Next-generation AI agent development platform. Create, enhance, and deploy 
          intelligent agents with advanced neural architectures and automated workflows.
        </p>
      </div>

      <div className="features-grid">
        {features.map((feature, index) => (
          <div 
            className="feature-card interactive-glow" 
            key={feature.id}
            style={{'--index': index}}
          >
            {feature.underConstruction && (
              <div className="construction-badge">
                <FaTools />
                <span>Beta Mode</span>
              </div>
            )}
            
            {feature.adminOnly && (
              <div className="construction-badge" style={{ 
                background: 'linear-gradient(45deg, var(--electric-cyan), var(--cyber-blue))' 
              }}>
                <FaBolt />
                <span>Admin Only</span>
              </div>
            )}
            
            <h3>
              {feature.icon}
              {feature.title}
            </h3>
            <p>{feature.description}</p>
            
            {/* Enhanced recent agents section */}
            {feature.showRecentAgents && (
              <div className="recent-agents-section">
                {loadingAgents ? (
                  <div className="recent-agents-loading">
                    <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
                    <span>Scanning agents...</span>
                  </div>
                ) : recentAgents.length > 0 ? (
                  <div className="recent-agents-list">
                    <div className="recent-agents-header">
                      <FaBolt style={{ marginRight: '0.5rem' }} />
                      Quick Access
                    </div>
                    {recentAgents.map((agent, agentIndex) => (
                      <Link
                        key={agent.uniqueIdentifier || agentIndex}
                        to={getAgentPath(agent)}
                        className="recent-agent-item"
                        title={`Access ${getAgentDisplayName(agent)} development environment`}
                      >
                        <FaRobot className="recent-agent-icon" />
                        <span className="recent-agent-name">
                          {getAgentDisplayName(agent)}
                        </span>
                        <FaArrowRight className="recent-agent-arrow" />
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            
            {feature.underConstruction ? (
              <div className="construction-notice">
                <FaExclamationTriangle />
                <span>Advanced features in development. Limited functionality available in beta mode.</span>
              </div>
            ) : (
              <Link to={feature.link} className="btn btn-primary">
                {feature.buttonText}
                <FaChevronRight />
              </Link>
            )}
          </div>
        ))}
      </div>
      
      {/* Decorative elements */}
      <div style={{
        position: 'fixed',
        top: '20%',
        right: '-200px',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(0, 229, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 1,
        animation: 'pulse 4s ease-in-out infinite'
      }} />
      
      <div style={{
        position: 'fixed',
        bottom: '10%',
        left: '-150px',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(41, 121, 255, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 1,
        animation: 'pulse 6s ease-in-out infinite reverse'
      }} />
    </div>
  );
}

export default Homepage;
